import {hpcAttrs} from '@github-ui/hpc'
import {testIdProps} from '@github-ui/test-id-props'
import {StopIcon} from '@primer/octicons-react'
import {Octicon} from '@primer/react/deprecated'
import {memo, useCallback, useEffect, useRef} from 'react'

import {DeletedViewToastUI} from './api/stats/contracts'
import {Board} from './components/board/board'
import {Blankslate} from './components/common/blankslate'
import {TableColumnsProviderForTable} from './components/table/state-providers/table-columns/table-columns-provider-for-table'
import {Table} from './components/table/table'
import {IconContainer, StyledToast, ToastAction, WarningIcon} from './components/toasts/toast'
import {ViewNavigation} from './components/view-navigation'
import {AgentTaskHydrator} from './features/copilot-tasks/use-hydrate-agent-tasks'
import {useFieldCommands} from './features/fields/hooks/use-field-commands'
import {useHorizontalGroupCommands} from './features/grouping/hooks/use-horizontal-group-commands'
import {useSliceBy} from './features/slicing/hooks/use-slice-by'
import {useSliceByCommands} from './features/slicing/hooks/use-slice-by-commands'
import {getInitialState} from './helpers/initial-state'
import {shortcutFromEvent, SHORTCUTS} from './helpers/keyboard-shortcuts'
import {ViewType} from './helpers/view-type'
import {useSortCommands} from './hooks/command-palette/use-sort-commands'
import {usePrefixedId} from './hooks/common/use-prefixed-id'
import {useBindMemexToDocument} from './hooks/use-bind-memex-to-document'
import {useEnabledFeatures} from './hooks/use-enabled-features'
import {useViewType} from './hooks/use-view-type'
import {useViews} from './hooks/use-views'
import {RoadmapView} from './pages/roadmap/components/roadmap-view'
import {TableColumnsProviderForRoadmap} from './pages/roadmap/table-columns-provider-for-roadmap'
import styles from './project-view.module.css'
import {KeyPressProvider} from './state-providers/keypress/key-press-provider'

export function ProjectView() {
  useBindMemexToDocument()

  const {memex_disable_autofocus} = useEnabledFeatures()

  const viewRef = useRef<ProjectViewInnerRef>(null)
  const focusIn = useCallback(() => viewRef.current?.focusIn(), [])

  // If the user presses the down arrow before focusing anything, jump focus into the current view
  useEffect(() => {
    if (!memex_disable_autofocus) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (shortcutFromEvent(event) === SHORTCUTS.ARROW_DOWN && document.activeElement === document.body) {
        event.preventDefault() // prevent scrolling down
        viewRef.current?.focusIn()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [memex_disable_autofocus])

  const projectViewId = usePrefixedId('project-view')
  return (
    <div id="memex-project-view-root" {...testIdProps('app-root')} className={styles.Box}>
      <ViewNavigation projectViewId={projectViewId} onFocusIntoCurrentView={focusIn} />
      <div id={projectViewId} role="tabpanel" className={styles.Box_1}>
        <MainView ref={viewRef} />
      </div>
    </div>
  )
}

interface ProjectViewInnerProps {
  type: ViewType
  ref?: React.Ref<ProjectViewInnerRef>
}

interface ProjectViewInnerRef {
  focusIn: () => void
}

export function ProjectViewInner({ref, type}: ProjectViewInnerProps) {
  switch (type) {
    case ViewType.Table:
      return (
        <KeyPressProvider>
          <TableColumnsProviderForTable>
            <Table ref={ref} />
          </TableColumnsProviderForTable>
        </KeyPressProvider>
      )

    case ViewType.Roadmap:
      return (
        <KeyPressProvider>
          <TableColumnsProviderForRoadmap>
            <RoadmapView ref={ref} />
          </TableColumnsProviderForRoadmap>
        </KeyPressProvider>
      )
    case ViewType.List:
    default:
      return (
        <KeyPressProvider>
          <Board ref={ref} />
        </KeyPressProvider>
      )
  }
}

const MainView = memo(function MainViewComponent({ref}: {ref?: React.Ref<ProjectViewInnerRef>}) {
  const {currentView, views, duplicateCurrentViewState} = useViews()
  const {
    projectLimits: {viewsLimit},
  } = getInitialState()
  const {viewType} = useViewType()
  const {sliceField} = useSliceBy()

  useSortCommands()
  useHorizontalGroupCommands()
  useFieldCommands()
  useSliceByCommands()

  if (!currentView) {
    return <NoViewFound />
  }

  return (
    <>
      {/*
       * Lazy-loads agent task state on the client when the backend sends
       * unhydrated tasks (no-op unless `memex_lazy_hydrate_agent_tasks` is
       * enabled). Rendered as a headless component so its React Query observers
       * re-render it rather than this whole view.
       */}
      <AgentTaskHydrator />
      {sliceField ? (
        <div className={styles.Box_5}>
          <div className={styles.Box_6}>
            <ProjectViewInner type={viewType} ref={ref} />
          </div>
        </div>
      ) : (
        <ProjectViewInner type={viewType} ref={ref} />
      )}
      {currentView.isDeleted ? (
        /**
         * We re-implement the toast here because we need a slightly tweaked
         * version of it, and want it to be static. We don't have a super easy
         * path to doing this re-using the definition of the Toast component currently,
         * but it might be a good place to unify these later
         */
        <StyledToast role="status" {...testIdProps('deleted-view-toast')}>
          <IconContainer className={styles.IconContainer}>{WarningIcon}</IconContainer>
          <div className={styles.Box_7}>
            <span className={styles.Text}>This view has been deleted.</span>
            {views.length < viewsLimit ? (
              <ToastAction
                {...testIdProps('deleted-view-toast-action')}
                onClick={() => {
                  duplicateCurrentViewState(currentView.number, undefined, {ui: DeletedViewToastUI})
                }}
                className={styles.ToastAction}
              >
                {currentView.isViewStateDirty ? 'Save a copy to make changes.' : 'Duplicate it to make changes.'}
              </ToastAction>
            ) : null}
          </div>
        </StyledToast>
      ) : null}
    </>
  )
})

const NoViewFound = memo(function NoViewFoundComponent() {
  return (
    <Blankslate {...hpcAttrs()} className={styles.Blankslate}>
      <Octicon icon={StopIcon} size={30} className={styles.Octicon} />
      <h2>This view no longer exists</h2>
      <p className={styles.Text_1}>Select another view to use this project.</p>
    </Blankslate>
  )
})
